import React, { useEffect, useState } from 'react';
import { owner, loyaltyPrograms } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Plus, Edit3, Trash2, X, Loader2, Gift, Lock, Star, Award, QrCode, Copy, Printer } from 'lucide-react';
import ClampWithMore from '../components/ClampWithMore';
import TierGate from '../components/TierGate';

const BLANK = { name:'', reward:'', points_per_visit: 10, points_to_redeem: 100, visits_required: 10, terms:'', expiration:'' };

export default function MyLoyalty() {
  return (
    <TierGate feature="loyalty">
      <MyLoyaltyInner />
    </TierGate>
  );
}

function MyLoyaltyInner() {
  const [myBiz, setMyBiz] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [gateMsg, setGateMsg] = useState('');
  const [showQr, setShowQr] = useState(false);
  const [qrInfo, setQrInfo] = useState(null);

  useEffect(() => {
    owner.myBusinesses().then(bs => {
      setMyBiz(bs);
      if (bs.length) setSelectedBiz(bs[0].id);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedBiz) return;
    setLoadingList(true); setGateMsg('');
    owner.listLoyalty(selectedBiz).then(setItems).catch(err => {
      setItems([]);
      if (err?.response?.status === 402) setGateMsg(err.response.data?.detail || 'Package required to manage loyalty programs.');
    }).finally(() => setLoadingList(false));
  }, [selectedBiz]);

  const openNew = () => { setForm(BLANK); setEditing('new'); };
  const openEdit = (l) => { setForm({ ...BLANK, ...l }); setEditing(l); };
  const close = () => setEditing(null);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name || !form.reward) { toast({ title: 'Name and reward are required' }); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        const created = await owner.addLoyalty(selectedBiz, form);
        setItems(prev => [created, ...prev]);
      } else {
        const updated = await owner.updateLoyalty(selectedBiz, editing.id, form);
        setItems(prev => prev.map(l => l.id === editing.id ? updated : l));
      }
      close();
      toast({ title: editing === 'new' ? 'Loyalty program created!' : 'Program updated' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this loyalty program?')) return;
    await owner.deleteLoyalty(selectedBiz, id);
    setItems(prev => prev.filter(l => l.id !== id));
  };

  const openQr = async () => {
    try {
      const info = await loyaltyPrograms.ownerQr(selectedBiz);
      setQrInfo(info);
      setShowQr(true);
    } catch (err) {
      toast({ title: 'QR unavailable', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  const checkinUrl = qrInfo ? `${window.location.origin}${qrInfo.checkinPath}` : '';
  const qrImgUrl = checkinUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(checkinUrl)}` : '';

  const copyUrl = async () => { try { await navigator.clipboard.writeText(checkinUrl); toast({ title: 'Link copied' }); } catch {} };
  const printQr = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>${qrInfo.businessName} — Loyalty QR</title><style>body{font-family:system-ui;text-align:center;padding:32px;color:#111}img{margin:12px auto;display:block}h1{margin:0 0 4px}p{color:#555;margin:4px 0}</style></head><body><h1>${qrInfo.businessName}</h1><p>${qrInfo.programName || 'Loyalty program'}</p><p><b>Reward:</b> ${qrInfo.reward || ''}</p><img src="${qrImgUrl.replace('280x280','480x480')}" alt="QR"/><p>Scan to check in — earn stamps toward your reward.</p><p style="font-size:11px;color:#888">${checkinUrl}</p></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>;

  if (myBiz.length === 0) return (
    <div className="pbb-card p-10 text-center max-w-xl mx-auto">
      <Gift className="mx-auto text-green-400" size={28}/>
      <div className="mt-3 font-semibold">Create a listing first</div>
      <p className="text-sm text-gray-400 mt-1">You need at least one business listing before creating loyalty programs.</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Loyalty Program</h1>
          <p className="text-gray-400 text-sm">Define how customers earn points and what they can redeem.</p>
        </div>
        <button onClick={openNew} disabled={!!gateMsg} className="px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm inline-flex items-center gap-2 disabled:opacity-50">
          <Plus size={16}/> New Program
        </button>
      </div>

      {selectedBiz && !gateMsg && items.length > 0 && (
        <div className="pbb-card p-4 flex items-center gap-3 flex-wrap" data-testid="qr-launcher">
          <QrCode className="text-green-400" size={22}/>
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold text-sm">In-store check-in QR</div>
            <div className="text-xs text-gray-400">Print & display at your counter. Customers scan to add a stamp toward your reward.</div>
          </div>
          <button onClick={openQr} data-testid="show-qr-btn" className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm inline-flex items-center gap-2">
            <QrCode size={14}/> Show QR
          </button>
        </div>
      )}

      <div className="pbb-card p-4 flex items-center gap-3 flex-wrap">
        <div className="text-sm text-gray-400">For business:</div>
        <select className="pbb-input" style={{ maxWidth: 320 }} value={selectedBiz} onChange={e => setSelectedBiz(e.target.value)}>
          {myBiz.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {gateMsg ? (
        <div className="pbb-card p-8 text-center">
          <Lock className="mx-auto text-green-400" size={26}/>
          <div className="mt-3 font-semibold">Package Required</div>
          <p className="text-sm text-gray-400 mt-1">{gateMsg}</p>
          <a href="/business/upgrade" className="pbb-btn-green mt-5 inline-block max-w-xs">View packages</a>
        </div>
      ) : loadingList ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading programs...</div>
      ) : items.length === 0 ? (
        <div className="pbb-card p-10 text-center">
          <Gift className="mx-auto text-green-400" size={26}/>
          <div className="mt-3 font-semibold">No loyalty programs yet</div>
          <p className="text-sm text-gray-400 mt-1">Create one to reward returning customers.</p>
          <button onClick={openNew} className="pbb-btn-green mt-5 max-w-xs mx-auto">Set up your first program</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map(l => (
            <div key={l.id} className="pbb-card p-5">
              <div className="flex items-center gap-2 text-green-400"><Gift size={18}/> <span className="font-semibold">{l.name}</span></div>
              <ClampWithMore text={l.reward} lines={3} className="text-sm text-gray-200 mt-2" testId={`my-loyalty-reward-${l.id}`} />
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] p-3">
                  <div className="text-gray-400">Earn</div>
                  <div className="text-green-400 font-semibold text-base">{l.points_per_visit || 10} pts</div>
                  <div className="text-gray-500">per visit</div>
                </div>
                <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] p-3">
                  <div className="text-gray-400">Redeem at</div>
                  <div className="text-green-400 font-semibold text-base">{l.points_to_redeem || 100} pts</div>
                  <div className="text-gray-500">for reward</div>
                </div>
              </div>
              {l.terms && <ClampWithMore text={`Terms: ${l.terms}`} lines={3} className="text-xs text-gray-500 mt-3" testId={`my-loyalty-terms-${l.id}`} />}
              {l.expiration && <div className="text-xs text-gray-500 mt-1">Program ends: {l.expiration}</div>}
              <div className="grid grid-cols-2 gap-2 mt-4">
                <button onClick={() => openEdit(l)} className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-xs inline-flex items-center justify-center gap-1"><Edit3 size={12}/> Edit</button>
                <button onClick={() => remove(l.id)} className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-red-500 hover:text-red-400 text-xs inline-flex items-center justify-center gap-1"><Trash2 size={12}/> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4" onClick={close}>
          <div className="pbb-card w-full max-w-lg p-5 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-lg">{editing === 'new' ? 'New Loyalty Program' : 'Edit Program'}</div>
              <button onClick={close} className="text-gray-400"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Program name *</label>
                <input className="pbb-input" placeholder="e.g. Coffee Lovers Club" value={form.name} onChange={e => setF('name', e.target.value)}/>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Reward *</label>
                <input className="pbb-input" placeholder="e.g. Free drink of your choice" value={form.reward} onChange={e => setF('reward', e.target.value)}/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1"><Award size={11}/> Points earned per visit</label>
                  <input type="number" min="1" className="pbb-input" value={form.points_per_visit} onChange={e => setF('points_per_visit', parseInt(e.target.value||'0', 10))}/>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1"><Gift size={11}/> Points needed to redeem</label>
                  <input type="number" min="1" className="pbb-input" value={form.points_to_redeem} onChange={e => setF('points_to_redeem', parseInt(e.target.value||'0', 10))}/>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Program end date (optional)</label>
                <input type="date" className="pbb-input" value={form.expiration || ''} onChange={e => setF('expiration', e.target.value)}/>
              </div>
              <textarea rows={2} className="pbb-input" placeholder="Terms & conditions" value={form.terms} onChange={e => setF('terms', e.target.value)}/>
              <div className="rounded-lg bg-green-600/5 border border-green-600/20 p-3 text-xs text-gray-300">
                Preview: Customers earn <b className="text-green-400">{form.points_per_visit || 0}</b> pts per visit and unlock <b className="text-green-400">{form.reward || 'your reward'}</b> at <b className="text-green-400">{form.points_to_redeem || 0}</b> pts.
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={close} className="flex-1 px-3 py-2.5 rounded-lg border border-[#2a2a2a] text-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="flex-1 pbb-btn-green disabled:opacity-70 inline-flex items-center justify-center gap-2">
                {saving && <Loader2 className="animate-spin" size={14}/>} {editing === 'new' ? 'Create' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showQr && qrInfo && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowQr(false)}>
          <div className="pbb-card w-full max-w-md p-5" onClick={e => e.stopPropagation()} data-testid="qr-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-lg inline-flex items-center gap-2"><QrCode size={18} className="text-green-400"/> Check-in QR</div>
              <button onClick={() => setShowQr(false)} className="text-gray-400"><X size={18}/></button>
            </div>
            <div className="rounded-xl bg-white p-4 flex items-center justify-center">
              {qrImgUrl ? <img src={qrImgUrl} alt="Loyalty QR" className="w-64 h-64" /> : <div className="text-gray-500">Generating…</div>}
            </div>
            <div className="mt-3 text-sm">
              <div className="font-semibold">{qrInfo.businessName}</div>
              <div className="text-gray-400 text-xs">{qrInfo.programName || 'No active program'} · Reward: {qrInfo.reward || '—'}</div>
              <div className="mt-2 text-[11px] text-gray-500 break-all bg-[#0d1f14] border border-[#153b23] rounded px-2 py-1.5">{checkinUrl}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button onClick={copyUrl} className="px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-sm inline-flex items-center justify-center gap-1"><Copy size={14}/> Copy link</button>
              <button onClick={printQr} className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm inline-flex items-center justify-center gap-1"><Printer size={14}/> Print</button>
            </div>
            {!qrInfo.hasProgram && (
              <div className="mt-3 text-xs text-yellow-400">Create a loyalty program above before customers can earn stamps.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
